import {
  AdminLoginResult,
  AdminMfaConfirmResponse,
  AdminMfaSetupResponse,
  AdminMfaStatus,
  ApiResponse,
  AdminUser,
  LoginCredentials,
  LoginResponse,
} from '@/types'
import { adminApiClient, queueAdminRefresh, runAdminAuthWrite } from '@/utils/apiClient'

import {
  captureAdminSession, clearAdminSession, registerAdminLoginResult, commitAdminLoginResult,
  requireCurrentAdminSession, type AdminSessionOwner,
} from '@/utils/adminAuthSession'

class AuthService {
  private readonly enrollmentOwners = new Map<string, AdminSessionOwner>()
  private readonly setupOwners = new Map<string, AdminSessionOwner>()

  private beginLogin(): AdminSessionOwner {
    clearAdminSession()
    this.enrollmentOwners.clear()
    this.setupOwners.clear()
    return captureAdminSession()
  }

  private rememberLogin(result: AdminLoginResult, owner: AdminSessionOwner): AdminLoginResult {
    requireCurrentAdminSession(owner)
    if ('mfaSetupRequired' in result) this.enrollmentOwners.set(result.enrollmentToken, owner)
    else this.publishLogin(result, owner)
    return result
  }

  private publishLogin(result: LoginResponse, owner: AdminSessionOwner): void {
    if (typeof result.user?.id !== 'string' || !result.user.id) {
      throw new Error('Invalid admin login user')
    }
    registerAdminLoginResult(result, owner)
    commitAdminLoginResult(result)
    registerAdminLoginResult(result, captureAdminSession())
  }

  async login(credentials: LoginCredentials): Promise<AdminLoginResult> {
    const owner = this.beginLogin()
    const response = await runAdminAuthWrite(owner, () =>
      adminApiClient.post<ApiResponse<AdminLoginResult>>('/admin/auth/login', credentials),
    accepted => { this.rememberLogin(accepted.data.data!, owner) },
    { waitForRefreshLock: true })
    return response.data.data!
  }

  async recoverLogin(credentials: {
    email: string
    password: string
    backupCode: string
  }): Promise<Extract<AdminLoginResult, { mfaSetupRequired: true }>> {
    const owner = this.beginLogin()
    const response = await runAdminAuthWrite(owner, () => adminApiClient.post<
      ApiResponse<Extract<AdminLoginResult, { mfaSetupRequired: true }>>
    >('/admin/auth/mfa/recover-login', credentials),
    accepted => { this.rememberLogin(accepted.data.data!, owner) },
    { waitForRefreshLock: true })
    return response.data.data!
  }

  async setupMfa(enrollmentToken?: string): Promise<AdminMfaSetupResponse> {
    const owner = enrollmentToken ? this.enrollmentOwners.get(enrollmentToken) : captureAdminSession()
    if (!owner) throw new Error('Admin MFA login flow changed')
    requireCurrentAdminSession(owner)
    const response = await adminApiClient.post<ApiResponse<AdminMfaSetupResponse>>(
      '/admin/auth/mfa/setup', enrollmentToken ? { enrollmentToken } : {})
    requireCurrentAdminSession(owner)
    this.setupOwners.set(response.data.data!.pendingToken, owner)
    return response.data.data!
  }

  async confirmMfa(data: {
    pendingToken: string
    totp: string
    enrollmentToken?: string
  }): Promise<AdminMfaConfirmResponse> {
    const owner = this.setupOwners.get(data.pendingToken)
    if (!owner) throw new Error('Admin MFA setup flow changed')
    requireCurrentAdminSession(owner)
    const response = await runAdminAuthWrite(owner, () => adminApiClient.post<
      ApiResponse<AdminMfaConfirmResponse>>('/admin/auth/mfa/confirm', data), accepted => {
      const result = accepted.data.data!
      // The cookie has changed; retire the old owner before another refresh can start.
      if (owner.sessionId) {
        // Keep the account page mounted so authenticated self-reset can show its backup code.
        this.publishLogin(result.login, owner)
      } else {
        clearAdminSession(owner)
        registerAdminLoginResult(result.login, captureAdminSession())
      }
      this.setupOwners.delete(data.pendingToken)
    })
    return response.data.data!
  }

  async getMfaStatus(): Promise<AdminMfaStatus> {
    const response = await adminApiClient.get<ApiResponse<AdminMfaStatus>>(
      '/admin/auth/mfa/status'
    )
    return response.data.data!
  }

  async verifyMfa(totp: string): Promise<{ verifiedAt: string }> {
    const response = await adminApiClient.post<ApiResponse<{ verifiedAt: string }>>(
      '/admin/auth/mfa/verify',
      { totp }
    )
    return response.data.data!
  }

  async resetMfa(): Promise<AdminMfaSetupResponse> {
    const owner = captureAdminSession()
    const response = await adminApiClient.post<ApiResponse<AdminMfaSetupResponse>>(
      '/admin/auth/mfa/reset',
      {}
    )
    requireCurrentAdminSession(owner)
    this.setupOwners.set(response.data.data!.pendingToken, owner)
    return response.data.data!
  }

  async regenerateBackupCode(): Promise<{ backupCode: string }> {
    const response = await adminApiClient.post<ApiResponse<{ backupCode: string }>>(
      '/admin/auth/mfa/backup-codes/regenerate',
      {}
    )
    return response.data.data!
  }

  async resetAdminMfa(adminUserId: string, reason: string): Promise<{
    adminUserId: string
    mfaEnabled: false
    sessionsRevoked: true
  }> {
    const response = await adminApiClient.post<
      ApiResponse<{
        adminUserId: string
        mfaEnabled: false
        sessionsRevoked: true
      }>
    >(`/admin/users/${adminUserId}/mfa/reset`, { reason })
    return response.data.data!
  }

  async getCurrentUser(): Promise<AdminUser> {
    const response = await adminApiClient.get<ApiResponse<AdminUser>>('/admin/auth/me')
    return response.data.data!
  }

  async validateToken(token: string): Promise<{ valid: boolean; user?: AdminUser }> {
    const response = await adminApiClient.post<ApiResponse<{ valid: boolean; user?: AdminUser }>>(
      '/admin/auth/validate',
      { token }
    )
    return response.data.data!
  }

  async logout(): Promise<void> {
    const sessionId = localStorage.getItem('admin_portal_session_id')
    const headers: Record<string, string> = {}
    if (sessionId) {
      headers['X-Session-Id'] = sessionId
    }
    await adminApiClient.post('/admin/auth/logout', {}, { headers })
  }

  async refreshToken(): Promise<{
    token: string
    expiresAt: string
    refreshToken?: string
    sessionId: string
  }> {
    return await queueAdminRefresh()
  }
}

export const authService = new AuthService()
